import { useEffect, useState, useCallback } from "react";
import Swal from "sweetalert2";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../css/Dashboard.css";
import "../css/Notifications.css";
import useIdleLogout from "../hooks/useIdleLogout";

/* ---------------- ENV ---------------- */
const LOGO_API = (process.env.REACT_APP_LOGO_IMAGES_API_URL || "").replace(/\/+$/, "");

// Derive image base from the API URL so relative paths like /uploads/... resolve correctly.
// e.g. https://backend.tabdhey.bt/admin/api/logo-images → https://backend.tabdhey.bt/admin
const LOGO_IMG_BASE = (() => {
  try {
    const u = new URL(LOGO_API || "https://backend.tabdhey.bt");
    const first = u.pathname.split("/").filter(Boolean)[0] || "";
    return first ? `${u.origin}/${first}` : u.origin;
  } catch {
    return "https://backend.tabdhey.bt/admin";
  }
})();

function resolveImgUrl(raw) {
  if (!raw) return null;
  if (/^https?:\/\//i.test(raw)) return raw;
  return `${LOGO_IMG_BASE}/${raw.replace(/^\/+/, "")}`;
}

/* ---------------- helpers ---------------- */
async function fetchJson(url, opts = {}, timeoutMs = 15000) {
  const controller = new AbortController();
  const t = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(url, { ...opts, signal: controller.signal });
    const text = await res.text();
    let json = null;
    try { json = text ? JSON.parse(text) : null; } catch { json = null; }
    if (!res.ok) {
      const msg =
        json?.message || json?.error || json?.errors?.[0] ||
        `Request failed (HTTP ${res.status})`;
      throw new Error(msg);
    }
    return json ?? {};
  } finally {
    clearTimeout(t);
  }
}

function getAuthHeaders() {
  const t = (localStorage.getItem("access_token") || "").trim();
  const h = { Accept: "application/json" };
  if (t) h.Authorization = `Bearer ${t}`;
  return h;
}

const mapLogo = (r) => {
  const rawUrl = r?.image ?? r?.image_url ?? r?.url ?? null;
  return {
    id: r?.id ?? null,
    name: r?.name ?? "",
    service_type: r?.service_type ?? "",
    image_raw: rawUrl,
    image: resolveImgUrl(rawUrl),
  };
};

export default function LogoImageManagement() {
  useIdleLogout();

  /* ---- create form ---- */
  const [name, setName] = useState("");
  const [serviceType, setServiceType] = useState("");
  const [imageFile, setImageFile] = useState(null);

  /* ---- list ---- */
  const [logos, setLogos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingNew, setSavingNew] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState("");

  /* ---- filter ---- */
  const [filterServiceType, setFilterServiceType] = useState("");

  /* ---- inline edit ---- */
  const [rowEditId, setRowEditId] = useState(null);
  const [rowDraft, setRowDraft] = useState(null);
  const [rowEditFile, setRowEditFile] = useState(null);
  const [rowEditPreview, setRowEditPreview] = useState(null);
  const [rowSaving, setRowSaving] = useState(false);

  const wideEdge = { marginInline: "clamp(-16px, -2.5vw, -32px)" };
  const TOP_PAD = { paddingInline: "clamp(16px, 2.5vw, 32px)" };

  /* ---- load ---- */
  const loadLogos = useCallback(async (svcFilter = "") => {
    if (!LOGO_API) {
      setError("Logo Images API not configured. Set REACT_APP_LOGO_IMAGES_API_URL.");
      setLogos([]);
      return;
    }
    try {
      setLoading(true);
      setError("");
      const url = new URL(LOGO_API, window.location.origin);
      if (svcFilter.trim()) url.searchParams.set("service_type", svcFilter.trim());
      const json = await fetchJson(url.toString(), { headers: getAuthHeaders() });
      const arr = Array.isArray(json?.data) ? json.data : Array.isArray(json) ? json : [];
      if (arr.length) console.log("[LogoMgmt] first record keys:", Object.keys(arr[0]), arr[0]);
      setLogos(arr.map(mapLogo));
    } catch (e) {
      setError(e.message || "Failed to load logo images.");
      setLogos([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadLogos(""); }, [loadLogos]);

  /* ---- create ---- */
  const handleSubmitNew = async (e) => {
    e.preventDefault();
    if (!LOGO_API) return Swal.fire({ icon: "error", title: "API not configured" });
    if (!name.trim()) return Swal.fire({ icon: "error", title: "Name is required" });
    if (!serviceType.trim()) return Swal.fire({ icon: "error", title: "Service type is required" });
    if (!imageFile) return Swal.fire({ icon: "error", title: "Image is required" });

    const fd = new FormData();
    fd.append("name", name.trim());
    fd.append("service_type", serviceType.trim());
    fd.append("image", imageFile);

    try {
      setSavingNew(true);
      await fetchJson(LOGO_API, { method: "POST", headers: getAuthHeaders(), body: fd });
      Swal.fire({ icon: "success", title: "Logo created", timer: 1200, showConfirmButton: false });
      setName("");
      setServiceType("");
      setImageFile(null);
      const inp = document.getElementById("logo-create-image");
      if (inp) inp.value = "";
      await loadLogos(filterServiceType);
    } catch (e2) {
      Swal.fire({ icon: "error", title: "Save failed", text: e2.message || "Failed to save." });
    } finally {
      setSavingNew(false);
    }
  };

  /* ---- inline edit ---- */
  const beginRowEdit = (r) => {
    setRowEditId(r.id);
    setRowDraft({ name: r.name, service_type: r.service_type });
    setRowEditFile(null);
    setRowEditPreview(null);
  };

  const cancelRowEdit = () => {
    if (rowEditPreview) URL.revokeObjectURL(rowEditPreview);
    setRowEditId(null);
    setRowDraft(null);
    setRowEditFile(null);
    setRowEditPreview(null);
  };

  const updateDraft = (key, val) =>
    setRowDraft((prev) => ({ ...(prev || {}), [key]: val }));

  const handleRowEditFileChange = (file) => {
    if (rowEditPreview) URL.revokeObjectURL(rowEditPreview);
    if (file) {
      setRowEditPreview(URL.createObjectURL(file));
      setRowEditFile(file);
    } else {
      setRowEditPreview(null);
      setRowEditFile(null);
    }
  };

  const saveRowEdit = async () => {
    if (!rowEditId || !rowDraft) return;
    if (!String(rowDraft.name || "").trim())
      return Swal.fire({ icon: "error", title: "Name is required" });
    if (!String(rowDraft.service_type || "").trim())
      return Swal.fire({ icon: "error", title: "Service type is required" });

    const fd = new FormData();
    fd.append("name", String(rowDraft.name).trim());
    fd.append("service_type", String(rowDraft.service_type).trim());
    if (rowEditFile) fd.append("image", rowEditFile);

    try {
      setRowSaving(true);
      await fetchJson(`${LOGO_API}/${encodeURIComponent(rowEditId)}`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: fd,
      });
      Swal.fire({ icon: "success", title: "Changes saved", timer: 1100, showConfirmButton: false });
      cancelRowEdit();
      await loadLogos(filterServiceType);
    } catch (e) {
      Swal.fire({ icon: "error", title: "Update failed", text: e.message || "Failed to update." });
    } finally {
      setRowSaving(false);
    }
  };

  /* ---- delete ---- */
  const handleDelete = async (id) => {
    if (!LOGO_API) return;
    const ok = await Swal.fire({
      icon: "warning",
      title: "Delete this logo?",
      text: `Logo ID: ${id}`,
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
    });
    if (!ok.isConfirmed) return;
    try {
      setDeletingId(id);
      await fetchJson(`${LOGO_API}/${encodeURIComponent(id)}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });
      Swal.fire({ icon: "success", title: "Deleted", timer: 1000, showConfirmButton: false });
      if (rowEditId === id) cancelRowEdit();
      await loadLogos(filterServiceType);
    } catch (e) {
      Swal.fire({ icon: "error", title: "Delete failed", text: e.message || "Failed to delete." });
      await loadLogos(filterServiceType);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="p-4 flex-grow-1">
      <style>{`
        .logo-table th { white-space: nowrap; }
        .logo-table { table-layout: fixed; }
        .logo-table td, .logo-table th { vertical-align: middle; }

        .logo-inline-input,
        .logo-inline-select {
          width: 100% !important;
          height: 32px !important;
          padding: 4px 10px !important;
          border-radius: 12px !important;
          box-shadow: none !important;
          min-width: 0 !important;
        }

        .logo-edit-cell { overflow: hidden; }

        .logo-actions .btn,
        .logo-actions button { box-shadow: none !important; }
        .logo-actions { display: inline-flex; gap: 8px; align-items: center; flex-wrap: nowrap; }
        .logo-actions .btn {
          border-radius: 6px !important;
          padding: 4px 14px !important;
          font-weight: 600 !important;
          font-size: 13px !important;
          min-width: 70px;
          background-color: transparent !important;
        }
        /* Restore correct colors overridden by contrast-table */
        .logo-actions .btn-outline-success {
          color: #198754 !important;
          border-color: #198754 !important;
          background-color: transparent !important;
        }
        .logo-actions .btn-outline-success:hover {
          background-color: #198754 !important;
          color: #fff !important;
        }
        .logo-actions .btn-outline-secondary {
          color: #6c757d !important;
          border-color: #6c757d !important;
          background-color: transparent !important;
        }
        .logo-actions .btn-outline-secondary:hover {
          background-color: #6c757d !important;
          color: #fff !important;
        }
        .logo-actions .btn-outline-primary {
          color: #0d6efd !important;
          border-color: #0d6efd !important;
          background-color: transparent !important;
        }
        .logo-actions .btn-outline-primary:hover {
          background-color: #0d6efd !important;
          color: #fff !important;
        }
        .logo-actions .btn-outline-danger {
          color: #dc3545 !important;
          border-color: #dc3545 !important;
          background-color: transparent !important;
        }
        .logo-actions .btn-outline-danger:hover {
          background-color: #dc3545 !important;
          color: #fff !important;
        }

        .col-logo-sl   { width: 70px; }
        .col-logo-id   { width: 90px; }
        .col-logo-name { width: 180px; }
        .col-logo-type { width: 140px; }
        .col-logo-img  { width: 110px; }
        .col-logo-url  { width: 260px; }
        .col-logo-act  { width: 200px; }

        .logo-url-cell {
          max-width: 260px;
          overflow: hidden;
        }
        .logo-url-text {
          font-size: 11px;
          color: #6c757d;
          word-break: break-all;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .logo-url-link {
          font-size: 11px;
          color: #0d6efd;
          text-decoration: none;
          word-break: break-all;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .logo-url-link:hover { text-decoration: underline; }

        .logo-thumb {
          width: 52px; height: 52px;
          object-fit: contain;
          border-radius: 8px;
          border: 1px solid #dee2e6;
          background: #f8f9fa;
          display: block;
        }
        .logo-thumb-placeholder {
          width: 52px; height: 52px;
          display: inline-flex; align-items: center; justify-content: center;
          background: #f1f3f5;
          border-radius: 8px;
          border: 1px dashed #ced4da;
          color: #adb5bd;
          font-size: 10px;
          text-align: center;
        }

        /* Hidden real file input; styled label acts as the trigger */
        .logo-replace-btn {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          cursor: pointer;
          font-size: 12px;
          padding: 3px 10px;
          border-radius: 8px;
          border: 1px solid #ced4da;
          background: #f8f9fa;
          color: #495057;
          font-weight: 500;
          white-space: nowrap;
          transition: background 0.15s;
          margin-top: 4px;
        }
        .logo-replace-btn:hover { background: #e9ecef; }
        .logo-replace-btn input[type="file"] { display: none; }
        .logo-new-filename {
          font-size: 10px;
          color: #6c757d;
          margin-top: 2px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 110px;
        }
      `}</style>

      <div className="card p-4 system-card">
        <h4 className="section-title">Logo / Image Management</h4>
        <p className="text-muted mb-4">
          Upload and manage service logos and images.
        </p>

        {/* ---- Create form ---- */}
        <form onSubmit={handleSubmitNew} className="settings-form">
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">Name *</label>
              <input
                type="text"
                className="form-control"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Food Logo"
                required
              />
            </div>

            <div className="col-md-4">
              <label className="form-label">Service Type *</label>
              <input
                type="text"
                className="form-control"
                value={serviceType}
                onChange={(e) => setServiceType(e.target.value)}
                placeholder="e.g. food, transport, mart"
                required
              />
            </div>

            <div className="col-md-4">
              <label className="form-label">Image *</label>
              <input
                id="logo-create-image"
                type="file"
                className="form-control"
                accept="image/*"
                onChange={(e) => setImageFile(e.target.files[0] || null)}
                required
              />
              {imageFile && (
                <small className="text-muted">{imageFile.name}</small>
              )}
            </div>
          </div>

          <button
            type="submit"
            className="btn btn-primary w-100 mt-4"
            disabled={savingNew}
          >
            {savingNew ? "Uploading…" : "Add Logo / Image"}
          </button>
        </form>

        {/* ---- Table ---- */}
        <div className="mt-4" style={wideEdge}>
          {/* Header: title left, filter right */}
          <div
            className="d-flex align-items-center justify-content-between mb-2"
            style={TOP_PAD}
          >
            <h5 className="mb-0">Existing Logos / Images</h5>

            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                type="text"
                className="form-control form-control-sm"
                style={{ width: 200 }}
                placeholder="Filter by service type"
                value={filterServiceType}
                onChange={(e) => setFilterServiceType(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") loadLogos(filterServiceType); }}
              />
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={() => loadLogos(filterServiceType)}
              >
                Filter
              </button>
              {filterServiceType && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => { setFilterServiceType(""); loadLogos(""); }}
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {error && (
            <div className="alert alert-danger mt-2 mb-3" style={TOP_PAD}>
              <small>{error}</small>
            </div>
          )}

          <div className="table-responsive contrast-surface" style={TOP_PAD}>
            <table className="table table-sm align-middle contrast-table w-100 logo-table">
              <thead>
                <tr>
                  <th className="col-logo-sl">Sl.No</th>
                  <th className="col-logo-id">ID</th>
                  <th className="col-logo-name">Name</th>
                  <th className="col-logo-type">Service Type</th>
                  <th className="col-logo-img">Image</th>
                  <th className="col-logo-url">Image URL</th>
                  <th className="col-logo-act">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="text-muted text-center py-3">Loading…</td>
                  </tr>
                ) : logos.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-muted text-center py-3">No logos found.</td>
                  </tr>
                ) : (
                  logos.map((r, idx) => {
                    const isEditing = rowEditId === r.id;
                    const busyDelete = deletingId === r.id;
                    const d = rowDraft || {};

                    return (
                      <tr key={r.id ?? idx}>
                        <td>{idx + 1}</td>
                        <td className="fw-semibold">{r.id}</td>

                        {/* Name */}
                        <td className="logo-edit-cell">
                          {isEditing ? (
                            <input
                              className="form-control form-control-sm logo-inline-input"
                              value={d.name ?? ""}
                              onChange={(e) => updateDraft("name", e.target.value)}
                            />
                          ) : (
                            r.name
                          )}
                        </td>

                        {/* Service Type */}
                        <td className="logo-edit-cell">
                          {isEditing ? (
                            <input
                              className="form-control form-control-sm logo-inline-input"
                              value={d.service_type ?? ""}
                              onChange={(e) => updateDraft("service_type", e.target.value)}
                            />
                          ) : (
                            r.service_type
                          )}
                        </td>

                        {/* Image */}
                        <td className="logo-edit-cell">
                          {isEditing ? (
                            <div>
                              {(rowEditPreview || r.image) && (
                                <img
                                  src={rowEditPreview || r.image}
                                  alt=""
                                  className="logo-thumb"
                                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                                />
                              )}
                              <label className="logo-replace-btn">
                                <i className="bi bi-upload"></i>
                                {rowEditFile ? "Change" : "Replace"}
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => handleRowEditFileChange(e.target.files[0] || null)}
                                />
                              </label>
                              {rowEditFile && (
                                <div className="logo-new-filename">{rowEditFile.name}</div>
                              )}
                            </div>
                          ) : r.image ? (
                            <img
                              src={r.image}
                              alt=""
                              className="logo-thumb"
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                                if (e.currentTarget.nextSibling)
                                  e.currentTarget.nextSibling.style.display = "inline-flex";
                              }}
                            />
                          ) : null}
                          {r.image && (
                            <span
                              className="logo-thumb-placeholder"
                              style={{ display: "none" }}
                            >
                              <i className="bi bi-image text-muted" style={{ fontSize: 20 }}></i>
                            </span>
                          )}
                          {!r.image && (
                            <span className="logo-thumb-placeholder">
                              <i className="bi bi-image text-muted" style={{ fontSize: 20 }}></i>
                            </span>
                          )}
                        </td>

                        {/* Image URL */}
                        <td className="logo-url-cell">
                          {r.image_raw ? (
                            <a
                              href={r.image}
                              target="_blank"
                              rel="noreferrer"
                              className="logo-url-link"
                              title={r.image_raw}
                            >
                              {r.image_raw}
                            </a>
                          ) : (
                            <span className="logo-url-text">—</span>
                          )}
                        </td>

                        {/* Actions */}
                        <td className="logo-edit-cell">
                          <div className="logo-actions">
                            {isEditing ? (
                              <>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-success"
                                  onClick={saveRowEdit}
                                  disabled={rowSaving}
                                >
                                  {rowSaving ? "Saving..." : "Save"}
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={cancelRowEdit}
                                  disabled={rowSaving}
                                >
                                  Cancel
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => beginRowEdit(r)}
                                  disabled={!!(rowEditId && rowEditId !== r.id)}
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => handleDelete(r.id)}
                                  disabled={busyDelete || !!(rowEditId && rowEditId !== r.id)}
                                >
                                  {busyDelete ? "..." : "Delete"}
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <footer className="dashboard-footer mt-4">
        <small>
          © {new Date().getFullYear()} <strong>སྟབས་བདེ།</strong> Admin • All rights reserved
        </small>
      </footer>
    </div>
  );
}
